import { FC, useEffect, useState } from 'react'

import { RenderIf } from 'components/RenderIf'
import { useTranslations } from 'featureSliced/shared/hooks/useTranslations'
import { qrMenuGET } from 'featureSliced/shared/api/qrMenu/GET'
import {
  IAllergen,
  IProductInfo,
} from 'featureSliced/shared/store/reducers/CategoriesReducer/types'
import { Allergens } from 'featureSliced/shared/hooks/openModals/useOpenProductModal/ComonProduct/Allergens'
import { Description } from 'featureSliced/shared/hooks/openModals/useOpenProductModal/ComonProduct/Description'

import { RichRuns } from './RichRuns'
import { NutrientTable } from './NutrientTable'
import {
  InfoSection,
  InfoSectionTitle,
  ProductInfoContainer,
  StatusText,
} from './styled'

type ProductInfoModalProps = {
  categoryId: number
  productId: number
  // false = the article has no declaration/nutrient item; render the catalogue
  // sections only and never ask the API (the item would be a guaranteed 404)
  fetchDetails?: boolean
  // sub-article sheet: allergens + note travel with the catalogue entry and are
  // rendered here; the top-level product shows them in the modal body instead
  withCatalogueSections?: boolean
  allergens?: IAllergen[]
  allergenLabels?: string[]
  note?: string | null
}

export const ProductInfoModal: FC<ProductInfoModalProps> = ({
  categoryId,
  productId,
  fetchDetails = true,
  withCatalogueSections = false,
  allergens,
  allergenLabels,
  note,
}) => {
  const t = useTranslations()

  const [info, setInfo] = useState<IProductInfo | null>(null)
  const [isLoading, setIsLoading] = useState(fetchDetails)
  const [isError, setIsError] = useState(false)

  useEffect(() => {
    if (!fetchDetails) return undefined
    let isMounted = true
    qrMenuGET
      .productInfo(categoryId, productId)
      .then((data) => {
        if (!isMounted) return
        setInfo(data)
        setIsLoading(false)
      })
      .catch(() => {
        if (!isMounted) return
        setIsError(true)
        setIsLoading(false)
      })
    return () => {
      isMounted = false
    }
  }, [categoryId, productId, fetchDetails])

  const sections = [
    {
      key: 'allergens',
      // the block renders its own heading
      title: null,
      isVisible: withCatalogueSections && Boolean(allergens?.length),
      content: (
        <Allergens
          product={{
            allergens: allergens || [],
            allergenLabels: allergenLabels || [],
          }}
        />
      ),
    },
    {
      key: 'ingredients',
      title: t('productInfo.ingredients'),
      isVisible: Boolean(info?.declaration?.length),
      content: <RichRuns runs={info?.declaration || []} />,
    },
    {
      key: 'nutrients',
      title: t('productInfo.nutrients'),
      isVisible: Boolean(info?.nutrients?.length),
      content: <NutrientTable rows={info?.nutrients || []} />,
    },
    {
      key: 'note',
      title: null,
      isVisible: withCatalogueSections && Boolean(note),
      content: <Description note={note ?? undefined} />,
    },
  ]

  return (
    <ProductInfoContainer data-testid="product_info_modal">
      <RenderIf condition={isLoading}>
        <StatusText>{t('common.loading')}</StatusText>
      </RenderIf>
      <RenderIf condition={isError}>
        <StatusText>{t('productInfo.loadError')}</StatusText>
      </RenderIf>
      {sections
        .filter((section) => section.isVisible)
        .map((section) => (
          <InfoSection key={section.key}>
            <RenderIf condition={section.title !== null}>
              <InfoSectionTitle>{section.title}</InfoSectionTitle>
            </RenderIf>
            {section.content}
          </InfoSection>
        ))}
    </ProductInfoContainer>
  )
}
